import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Copy } from "lucide-react";
import { formatMoney } from "@finora/config";
import { ApiError } from "@finora/api-client";
import { Button, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";
import { DateInputDDMMYYYY } from "../../components/DateInputDDMMYYYY";

export function ObradaIzvodaClientTab() {
  const queryClient = useQueryClient();
  const {
    data: addresses,
    isLoading,
    isError: isAddressesError,
    error: addressesError,
  } = useQuery({
    queryKey: ["my-statement-import-addresses"],
    queryFn: () => api.listMyStatementImportAddresses(),
    retry: false,
  });
  const { data: stats } = useQuery({
    queryKey: ["my-statement-processing-stats"],
    queryFn: () => api.getMyStatementProcessingStats(),
  });
  const { data: history } = useQuery({
    queryKey: ["my-statement-processing-history"],
    queryFn: () => api.listMyStatementProcessingHistory(),
  });

  const invalidateAddresses = () => queryClient.invalidateQueries({ queryKey: ["my-statement-import-addresses"] });

  const generate = useMutation({
    mutationFn: () => api.generateMyStatementImportAddress(),
    onSuccess: invalidateAddresses,
  });

  const activeAddresses = addresses?.filter((a) => a.status === "ACTIVE") ?? [];

  // Gejtovano iza "bank_import" modula (vidi statement-processing-client.
  // controller.ts) - isti prikaz kao svuda drugde gde backend vrati
  // MODULE_NOT_AVAILABLE.
  if (isAddressesError && addressesError instanceof ApiError && addressesError.response.errorCode === "MODULE_NOT_AVAILABLE") {
    return <ModuleGateNotice error={addressesError} moduleKey="bank_import" />;
  }

  return (
    <div className="space-y-4">
      <GmailImportCard />
      <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-800">Mail adresa za obradu izvoda</h2>
          {/* Dozvoljena je TAČNO JEDNA aktivna adresa po firmi (vezana za
              PIB) - vidi razgovor. Nema više samoposlužnog brisanja adrese
              (uklonjeno 24.09.2026, vidi razgovor "izbaci dugme za brisanje
              email adrese") - kad firma jednom ima aktivnu adresu, ostaje
              trajno takva; promenu/opoziv radi isključivo HAFiskal Admin
              (CompanyDetailPage -> tab "Obrada izvoda" -> "Regeneriši"). */}
          {activeAddresses.length === 0 && (
            <Button size="sm" disabled={generate.isPending} onClick={() => generate.mutate()}>
              <Plus className="h-3.5 w-3.5" aria-hidden /> Nova adresa
            </Button>
          )}
        </div>
        {generate.isError && (
          <p className="mb-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
            {generate.error instanceof ApiError ? generate.error.response.message : "Kreiranje adrese nije uspelo."}
          </p>
        )}
        {isLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : !addresses || addresses.length === 0 ? (
          <p className="text-sm text-slate-400">Još nema adrese za obradu izvoda.</p>
        ) : (
          <div className="space-y-1.5">
            {addresses.map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-lg bg-app-bg px-3 py-2 text-sm">
                <div className="flex items-center">
                  <span className="font-mono">{a.emailAddress}</span>
                  <button
                    onClick={() => navigator.clipboard.writeText(a.emailAddress).catch(() => undefined)}
                    className="ml-2 text-slate-400 hover:text-brand"
                    title="Kopiraj"
                  >
                    <Copy className="h-3.5 w-3.5" aria-hidden />
                  </button>
                  {a.label && <span className="ml-2 text-xs text-slate-400">({a.label})</span>}
                  <span
                    className={`ml-2 text-xs font-medium ${
                      a.status === "ACTIVE" ? "text-emerald-600" : "text-slate-400 line-through"
                    }`}
                  >
                    {a.status === "ACTIVE" ? "AKTIVAN" : "OPOZVAN"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
        {activeAddresses.length === 0 && addresses && addresses.length > 0 && (
          <p className="mt-2 text-xs text-amber-600">
            Nema aktivne adrese - sve su opozvane/obrisane. Klikni "Nova adresa".
          </p>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-5 gap-3">
          <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
            <p className="text-xs text-slate-400">Primljeno danas</p>
            <p className="text-xl font-semibold text-slate-900">{stats.received}</p>
          </div>
          <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
            <p className="text-xs text-slate-400">Uspešno obrađeno</p>
            <p className="text-xl font-semibold text-emerald-600">{stats.success}</p>
          </div>
          <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
            <p className="text-xs text-slate-400">Greške</p>
            <p className="text-xl font-semibold text-red-600">{stats.failed}</p>
          </div>
          <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
            <p className="text-xs text-slate-400">Duplikati</p>
            <p className="text-xl font-semibold text-amber-600">{stats.duplicates}</p>
          </div>
          <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
            <p className="text-xs text-slate-400">Potrošnja danas</p>
            <p className="text-xl font-semibold text-slate-900">{formatMoney(stats.totalCost)}</p>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        <table>
          <thead>
            <tr>
              <th>Datum</th>
              <th>Fajl</th>
              <th>Banka</th>
              <th>Transakcija</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {history?.map((h) => (
              <tr key={h.jobId}>
                <td className="text-slate-500">{new Date(h.createdAt).toLocaleString("sr-RS")}</td>
                <td className="font-medium text-slate-800">{h.filename ?? "—"}</td>
                <td>{h.bank ?? "—"}</td>
                <td>{h.transactionCount ?? "—"}</td>
                <td>
                  {h.status === "SUCCESS" ? (
                    <span className="text-emerald-600">Uspešno</span>
                  ) : (
                    <span className="cursor-help text-red-600" title={h.errorMessage ?? "Nema dodatnog opisa greške."}>
                      Neuspešno
                      {h.errorCode ? ` (${h.errorCode.replace(/^FINORA-/i, "")}${h.errorId ? ` / ${h.errorId}` : ""})` : ""}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {(!history || history.length === 0) && (
          <p className="p-4 text-sm text-slate-400">Još nema evidentirane obrade izvoda.</p>
        )}
      </div>
    </div>
  );
}

function GmailImportCard() {
  const queryClient = useQueryClient();
  const waitingRef = useRef(false);
  const startedAtRef = useRef<{ lastRunAt: string | null; at: number } | null>(null);
  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ["my-gmail-import"],
    queryFn: () => api.getMyGmailImport(),
    retry: false,
    refetchInterval: (q) => (q.state.data?.running || waitingRef.current ? 2000 : false),
  });
  const [provider, setProvider] = useState<"GMAIL" | "IMAP">("GMAIL");
  const [imapHost, setImapHost] = useState("");
  const [imapPort, setImapPort] = useState("993");
  const [imapSecure, setImapSecure] = useState(true);
  const [imapUser, setImapUser] = useState("");
  const [gmail, setGmail] = useState("");
  const [pass, setPass] = useState("");
  const [filters, setFilters] = useState("izvod broj\nIzvod po dinarskom racunu broj");
  const [since, setSince] = useState(() => new Date().toISOString().slice(0, 10));
  const [active, setActive] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setGmail(data.gmailAddress);
      setProvider(data.provider ?? "GMAIL");
      setImapHost(data.imapHost ?? "");
      setImapPort(String(data.imapPort ?? 993));
      setImapSecure(data.imapSecure ?? true);
      setImapUser(data.imapUser ?? "");
      setFilters(data.subjectFilters.join("\n"));
      setSince(data.sinceDate);
      setActive(data.isActive);
    }
  }, [data]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["my-gmail-import"] });
  // Kad se uvoz završi (pokrenut dugmetom), sam osveži podatke na stranici i prikaži rezultat.
  useEffect(() => {
    const s = startedAtRef.current;
    if (!waitingRef.current || !s || !data) return;
    const finished = !data.running && data.lastRunAt !== s.lastRunAt;
    const timedOut = Date.now() - s.at > 10 * 60_000;
    if (!finished && !timedOut) return;
    waitingRef.current = false;
    startedAtRef.current = null;
    if (finished) {
      setMsg(
        data.lastStatus === "ERROR"
          ? `Uvoz nije uspeo: ${data.lastError ?? "nepoznata greška"}`
          : data.lastImportedCount > 0
            ? `Uvoz završen: obrađeno mailova sa izvodom: ${data.lastImportedCount}.`
            : "Uvoz završen: nema novih izvoda.",
      );
    }
    // Obrada izvoda se završava malo posle uvoza - osvežavaj u talasima da se lista
    // izvoda i statistika pojave sami, bez ručnog osvežavanja stranice.
    const refreshAll = () => {
      for (const k of ["my-statement-processing-stats", "my-statement-processing-history", "bank-statements", "bank-statement", "my-statement-import-addresses", "my-gmail-import"]) {
        void queryClient.invalidateQueries({ queryKey: [k], refetchType: "all" });
      }
    };
    refreshAll();
    for (const ms of [4000, 10000, 20000, 40000]) setTimeout(refreshAll, ms);
  }, [data, queryClient]);
  const errText = (e: unknown) => (e instanceof ApiError ? e.message : "Greška");
  const save = useMutation({
    mutationFn: () =>
      api.saveMyGmailImport({
        provider,
        ...(provider === "IMAP"
          ? { imapHost, imapPort: Number(imapPort) || 993, imapSecure, imapUser: imapUser || undefined }
          : {}),
        gmailAddress: gmail,
        appPassword: pass || undefined,
        subjectFilters: filters.split("\n").map((f) => f.trim()).filter(Boolean),
        sinceDate: since,
        isActive: active,
      }),
    onSuccess: () => {
      setPass("");
      setMsg("Sačuvano.");
      void refresh();
    },
    onError: (e) => setMsg(errText(e)),
  });
  const test = useMutation({
    mutationFn: () => api.testMyGmailImport(),
    onSuccess: (r) =>
      setMsg(r.ok ? `Veza radi. Pronađeno mailova: ${r.matched ?? 0}${r.sample?.length ? " (npr. " + r.sample.join(" | ") + ")" : ""}` : r.error ?? "Neuspešno"),
    onError: (e) => setMsg(errText(e)),
  });
  const run = useMutation({
    mutationFn: () => api.runMyGmailImport(),
    onSuccess: () => {
      setMsg("Uvoz je pokrenut, sačekajte rezultat...");
      startedAtRef.current = { lastRunAt: data?.lastRunAt ?? null, at: Date.now() };
      waitingRef.current = true;
      setTimeout(() => void refresh(), 1000);
    },
    onError: (e) => setMsg(errText(e)),
  });
  const remove = useMutation({
    mutationFn: () => api.deleteMyGmailImport(),
    onSuccess: () => {
      setGmail("");
      setPass("");
      setMsg("Veza uklonjena.");
      void refresh();
    },
  });

  const input = "w-full rounded-lg border border-border bg-white px-3 py-1.5 text-sm";
  if (loadError instanceof ApiError && loadError.response.errorCode === "MODULE_NOT_AVAILABLE") {
    return (
      <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <h2 className="mb-2 text-sm font-semibold text-slate-800">Uvoz izvoda iz e-pošte</h2>
        <ModuleGateNotice error={loadError} moduleKey="gmail_import" />
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
      <h2 className="mb-1 text-sm font-semibold text-slate-800">Uvoz izvoda iz e-pošte</h2>
      <p className="mb-3 text-xs text-slate-400">
        Automatski povlači mailove sa izvodima iz vašeg sandučeta (Gmail ili drugi mail server) i obrađuje ih.
      </p>
      {isLoading ? (
        <Skeleton className="h-10 w-full" />
      ) : (
        <div className="space-y-2">
          <label className="block text-xs text-slate-500">
            Vrsta e-pošte
            <select className={input} value={provider} onChange={(e) => setProvider(e.target.value as "GMAIL" | "IMAP")}>
              <option value="GMAIL">Gmail</option>
              <option value="IMAP">Drugi mail server (IMAP)</option>
            </select>
          </label>
          {!data && provider === "IMAP" && (
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
              Upišite podatke iz uputstva vašeg mail provajdera: adresu IMAP servera (npr. mail.vasafirma.rs), port (obično 993) i lozinku od sandučeta.
            </div>
          )}
          {!data && provider === "GMAIL" && (
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
              <p className="mb-1 font-semibold">Kako da povežete Gmail (traje 2 minuta)</p>
              <ol className="list-decimal space-y-0.5 pl-4">
                <li>
                  Uključite „Potvrdu u 2 koraka“ na svom Google nalogu:{" "}
                  <a className="underline" href="https://myaccount.google.com/signinoptions/twosv" target="_blank" rel="noreferrer">
                    myaccount.google.com/signinoptions/twosv
                  </a>
                </li>
                <li>
                  Napravite lozinku za aplikaciju (upišite bilo koje ime, npr. „HAFiskal“):{" "}
                  <a className="underline" href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">
                    myaccount.google.com/apppasswords
                  </a>
                </li>
                <li>Google prikaže lozinku od 16 slova. Prepišite je ovde (razmaci ne smetaju). To NIJE obična lozinka od Gmail-a.</li>
              </ol>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-slate-500">
              {provider === "GMAIL" ? "Gmail adresa" : "E-mail adresa"}
              <input className={input} value={gmail} onChange={(e) => setGmail(e.target.value)} />
            </label>
            <label className="text-xs text-slate-500">
              {provider === "GMAIL" ? "Lozinka za aplikaciju (16 slova, ne obična lozinka)" : "Lozinka sandučeta"} {data ? "- ostavi prazno da ostane ista" : ""}
              <input className={input} type="password" autoComplete="new-password" value={pass} onChange={(e) => setPass(e.target.value)} />
            </label>
            {provider === "IMAP" && (
              <>
                <label className="text-xs text-slate-500">
                  IMAP server
                  <input className={input} placeholder="npr. mail.vasafirma.rs" value={imapHost} onChange={(e) => setImapHost(e.target.value)} />
                </label>
                <label className="text-xs text-slate-500">
                  Port
                  <input className={input} value={imapPort} onChange={(e) => setImapPort(e.target.value.replace(/\D/g, ""))} />
                </label>
                <label className="text-xs text-slate-500">
                  Korisničko ime (ako se razlikuje od adrese)
                  <input className={input} value={imapUser} onChange={(e) => setImapUser(e.target.value)} />
                </label>
                <label className="flex items-end gap-2 pb-1.5 text-xs text-slate-500">
                  <input type="checkbox" checked={imapSecure} onChange={(e) => setImapSecure(e.target.checked)} /> Sigurna veza (SSL/TLS)
                </label>
              </>
            )}
            <label className="text-xs text-slate-500">
              Uvezi izvode od datuma
              <DateInputDDMMYYYY className={input} value={since} onChange={setSince} />
            </label>
            <label className="flex items-end gap-2 pb-1.5 text-xs text-slate-500">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Aktivno
            </label>
          </div>
          <label className="block text-xs text-slate-500">
            Filter naslova (jedan po redu)
            <textarea className={input} rows={3} value={filters} onChange={(e) => setFilters(e.target.value)} />
          </label>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={save.isPending || !gmail || (provider === "IMAP" && !imapHost)} onClick={() => save.mutate()}>Sačuvaj</Button>
            {data && (
              <>
                <Button size="sm" disabled={test.isPending} onClick={() => test.mutate()}>Testiraj vezu</Button>
                <Button size="sm" disabled={run.isPending || data.running || waitingRef.current} onClick={() => run.mutate()}>
                  {data.running || waitingRef.current ? "Uvoz u toku..." : "Uvezi sada"}
                </Button>
                <button
                  className="text-xs text-slate-400 hover:text-red-600"
                  onClick={() => window.confirm("Ukloniti vezu sa e-poštom?") && remove.mutate()}
                >
                  Ukloni
                </button>
              </>
            )}
          </div>
          {msg && <p className="text-xs text-slate-600">{msg}</p>}
          {data && (
            <p className="text-xs text-slate-400">
              Poslednje pokretanje: {data.lastRunAt ? new Date(data.lastRunAt).toLocaleString("sr-RS") : "nikad"}
              {data.lastStatus ? ` · ${data.lastStatus}` : ""} · poslednji put: {data.lastImportedCount} · ukupno: {data.totalImportedCount}
              {data.lastError ? ` · greška: ${data.lastError}` : ""}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
